import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ClearAllOutlinedIcon from '@mui/icons-material/ClearAllOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  fetchTenantImportTables,
  previewTenantDataImport,
  runTenantDataImport,
} from '../../services/platformDataImportApi';
import {
  getSelectedPlatformTenant,
  listPlatformTenants,
  mapLaravelError,
  selectPlatformTenant,
} from '../../services/platformTenantsApi';

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeColumnKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const preferredUniqueColumns = ['employee_code', 'email', 'code', 'iqama_no', 'id'];

const buildInitialMappings = (headers, columns) => {
  const editableColumns = asArray(columns).filter((column) => column?.is_editable);
  const columnsByNormalizedName = new Map(
    editableColumns.map((column) => [normalizeColumnKey(column.name), column.name])
  );
  const usedDbColumns = new Set();

  return asArray(headers).map((header) => {
    const normalizedHeader = normalizeColumnKey(header?.label || header?.column);
    const matchedColumn = columnsByNormalizedName.get(normalizedHeader);

    if (matchedColumn && !usedDbColumns.has(matchedColumn)) {
      usedDbColumns.add(matchedColumn);
      return {
        excel_column: header.column,
        db_column: matchedColumn,
      };
    }

    return {
      excel_column: header.column,
      db_column: '',
    };
  });
};

const findDuplicateTargets = (mappings) => {
  const seen = new Set();
  const duplicates = new Set();

  asArray(mappings).forEach((mapping) => {
    const target = mapping?.db_column;
    if (!target) return;
    if (seen.has(target)) duplicates.add(target);
    seen.add(target);
  });

  return duplicates;
};

const chooseDefaultUniqueColumn = (columns, mappings) => {
  const mappedTargets = new Set(asArray(mappings).map((mapping) => mapping.db_column).filter(Boolean));
  const editableNames = new Set(asArray(columns).filter((column) => column?.is_editable).map((column) => column.name));

  return preferredUniqueColumns.find((column) => mappedTargets.has(column) && editableNames.has(column))
    || asArray(mappings).find((mapping) => mapping.db_column && editableNames.has(mapping.db_column))?.db_column
    || '';
};

const formatColumnMeta = (column) => {
  const parts = [];
  if (column?.type) parts.push(column.type);
  if (column?.is_required) parts.push('required');
  return parts.join(' · ');
};

const safeFilePart = (value) =>
  String(value || 'template')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'template';

const TEMPLATE_ROW_LIMIT = 500;
const OPTIONS_SHEET_NAME = 'Dropdown_Options';

const STATIC_TEMPLATE_OPTIONS = {
  users: {
    role: [
      'employee',
      'hr_admin',
      'manager',
      'ceo',
      'recruiter',
      'finance',
      'finance_coordinator',
      'operation_manager',
      'operations_manager',
      'executive_md',
      'super_admin',
    ],
    status: ['active', 'inactive'],
    gender: ['Male', 'Female'],
  },
  tenants: {
    status: ['active', 'inactive', 'archived'],
    auth_mode: ['password'],
  },
};

const CORE_TEMPLATE_COLUMNS = {
  users: [
    'employee_code',
    'name',
    'email',
    'role',
    'actual_role',
    'status',
    'department_id',
    'project_id',
    'manager_id',
    'position',
    'joining_date',
  ],
  departments: ['name', 'code', 'manager_id'],
  projects: ['name', 'latitude', 'longitude', 'allowed_radius_in_meters'],
};

const SYSTEM_DEFAULT_TEMPLATE_COLUMNS = {
  users: ['password'],
};

const optionValue = (option) => {
  const rawValue = option && typeof option === 'object' ? option.value : option;
  return String(rawValue ?? '').trim();
};

const optionLabel = (option) => {
  if (option && typeof option === 'object') {
    return String(option.label ?? option.value ?? '').trim();
  }

  return optionValue(option);
};

const uniqueOptions = (values) => {
  const seen = new Set();

  return asArray(values)
    .map((option) => ({
      value: optionValue(option),
      label: optionLabel(option) || optionValue(option),
    }))
    .filter((option) => {
      const value = option.value;
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const optionsForColumn = (tableName, column) => {
  const schemaOptions = uniqueOptions(column?.options);
  if (schemaOptions.length > 0) {
    return schemaOptions;
  }

  return uniqueOptions(STATIC_TEMPLATE_OPTIONS[tableName]?.[column?.name]);
};

const templateColumnsFor = (tableName, columns, columnMode) => {
  const editableColumns = asArray(columns).filter((column) => column?.is_editable);
  if (columnMode !== 'required_core') {
    return editableColumns;
  }

  const coreColumns = new Set(asArray(CORE_TEMPLATE_COLUMNS[tableName]));
  const defaultedColumns = new Set(asArray(SYSTEM_DEFAULT_TEMPLATE_COLUMNS[tableName]));
  const filteredColumns = editableColumns.filter((column) => (
    !defaultedColumns.has(column.name) && (column.is_required || coreColumns.has(column.name))
  ));

  return filteredColumns.length > 0 ? filteredColumns : editableColumns;
};

const allowedValuesSummary = (tableName, column) => {
  const options = optionsForColumn(tableName, column);
  if (options.length === 0) {
    return '';
  }

  if (options.length > 12) {
    return `${options.length} values in ${OPTIONS_SHEET_NAME}`;
  }

  return options.map((option) => option.value).join(', ');
};

const columnNote = (tableName, column) => {
  if (tableName === 'users' && column?.name === 'employee_code') {
    return 'Enter the new unique employee number. This is not limited to existing employees.';
  }

  if (tableName === 'users' && column?.name === 'password') {
    return 'Optional. Blank values use the system default password for new users.';
  }

  if (tableName === 'users' && column?.name === 'role') {
    return 'Use the application role key, for example hr_admin, manager, ceo, or employee.';
  }

  return column?.is_required ? 'Required for new rows unless the database supplies a value.' : '';
};

const buildGuideRows = (tableName, columns) => [
  ['Column', 'Label', 'Required', 'Type', 'Nullable', 'Default', 'Allowed Values', 'Notes'],
  ...asArray(columns).map((column) => [
    column.name,
    column.label || '',
    column.is_required ? 'Yes' : 'No',
    column.type || '',
    column.nullable ? 'Yes' : 'No',
    column.default ?? '',
    allowedValuesSummary(tableName, column),
    columnNote(tableName, column),
  ]),
];

const excelColumnName = (index) => {
  let value = index;
  let name = '';

  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }

  return name;
};

const escapeWorksheetName = (name) => String(name).replace(/'/g, "''");

const worksheetRangeFormula = (sheetName, columnIndex, startRow, endRow) => {
  const columnLetter = excelColumnName(columnIndex);
  return `'${escapeWorksheetName(sheetName)}'!$${columnLetter}$${startRow}:$${columnLetter}$${endRow}`;
};

const applyHeaderStyle = (row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1F3D7C' },
    };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
};

const setExcelColumnWidths = (worksheet, rows) => {
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);

  worksheet.columns = Array.from({ length: columnCount }, (_, columnIndex) => {
    const maxLength = rows.reduce((max, row) => {
      const value = row[columnIndex];
      return Math.max(max, String(value ?? '').length);
    }, 0);

    return { width: Math.min(Math.max(maxLength + 2, 12), 38) };
  });
};

const buildDropdownOptionSheet = (workbook, tableName, columns) => {
  const dropdownRanges = new Map();
  let optionsSheet = null;
  let nextColumnIndex = 1;

  asArray(columns).forEach((column) => {
    const options = optionsForColumn(tableName, column);
    if (options.length === 0) return;

    if (!optionsSheet) {
      optionsSheet = workbook.addWorksheet(OPTIONS_SHEET_NAME, {
        views: [{ state: 'frozen', ySplit: 1 }],
      });
    }

    const valueColumnIndex = nextColumnIndex;
    const labelColumnIndex = nextColumnIndex + 1;
    optionsSheet.getCell(1, valueColumnIndex).value = `${column.name} value`;
    optionsSheet.getCell(1, labelColumnIndex).value = `${column.name} label`;

    options.forEach((option, optionIndex) => {
      const rowIndex = optionIndex + 2;
      optionsSheet.getCell(rowIndex, valueColumnIndex).value = option.value;
      optionsSheet.getCell(rowIndex, labelColumnIndex).value = option.label;
    });

    optionsSheet.getColumn(valueColumnIndex).width = Math.min(Math.max(column.name.length + 8, 14), 28);
    optionsSheet.getColumn(labelColumnIndex).width = 36;
    dropdownRanges.set(
      column.name,
      worksheetRangeFormula(OPTIONS_SHEET_NAME, valueColumnIndex, 2, options.length + 1)
    );
    nextColumnIndex += 3;
  });

  if (optionsSheet) {
    applyHeaderStyle(optionsSheet.getRow(1));
  }

  return dropdownRanges;
};

const downloadWorkbookBuffer = (buffer, fileName) => {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const ResultAlert = ({ result }) => {
  if (!result) return null;

  const hasErrors = Number(result.errors_count || 0) > 0;

  return (
    <Alert
      severity={hasErrors ? 'warning' : 'success'}
      icon={hasErrors ? <WarningAmberOutlinedIcon /> : <CheckCircleOutlineOutlinedIcon />}
    >
      <Stack spacing={0.75}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {result.message || 'Import completed.'}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="success" label={`${result.inserted || 0} inserted`} />
          <Chip size="small" color="info" label={`${result.updated || 0} updated`} />
          <Chip size="small" color={hasErrors ? 'warning' : 'default'} label={`${result.skipped || 0} skipped`} />
        </Stack>
      </Stack>
    </Alert>
  );
};

export default function TenantDataImportPage() {
  const storedTenant = getSelectedPlatformTenant();
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(storedTenant?.id || '');
  const [tables, setTables] = useState([]);
  const [table, setTable] = useState('');
  const [file, setFile] = useState(null);
  const [headingRow, setHeadingRow] = useState(1);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [mode, setMode] = useState('insert');
  const [uniqueBy, setUniqueBy] = useState('');
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [result, setResult] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingTables, setLoadingTables] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exportingTemplate, setExportingTemplate] = useState(false);
  const [templateColumnMode, setTemplateColumnMode] = useState('all');
  const [error, setError] = useState('');

  const resetPreview = useCallback(() => {
    setPreview(null);
    setMappings([]);
    setResult(null);
    setUniqueBy('');
  }, []);

  const loadTenants = useCallback(() => {
    let active = true;

    const load = async () => {
      setLoadingTenants(true);
      setError('');

      try {
        const payload = await listPlatformTenants();
        if (!active) return;

        const rows = asArray(payload);
        setTenants(rows);

        if (!tenantId && rows.length > 0) {
          const activeTenant = rows.find((tenant) => String(tenant?.status || '').toLowerCase() === 'active') || rows[0];
          setTenantId(activeTenant?.id ? String(activeTenant.id) : '');
          if (activeTenant) selectPlatformTenant(activeTenant);
        }
      } catch (requestError) {
        if (active) setError(mapLaravelError(requestError).message);
      } finally {
        if (active) setLoadingTenants(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [tenantId]);

  useEffect(() => loadTenants(), [loadTenants]);

  useEffect(() => {
    if (!tenantId) {
      setTables([]);
      setTable('');
      resetPreview();
      return undefined;
    }

    let active = true;

    const loadTables = async () => {
      setLoadingTables(true);
      setError('');
      resetPreview();

      try {
        const payload = await fetchTenantImportTables(tenantId);
        if (!active) return;

        const rows = asArray(payload?.tables);
        setTables(rows);

        setTable((currentTable) => {
          const currentTableStillExists = rows.some((item) => item.name === currentTable);
          if (currentTableStillExists) {
            return currentTable;
          }

          const defaultTable = rows.find((item) => item.name === 'users') || rows[0] || null;
          return defaultTable?.name || '';
        });
      } catch (requestError) {
        if (!active) return;
        setTables([]);
        setTable('');
        setError(mapLaravelError(requestError).message);
      } finally {
        if (active) setLoadingTables(false);
      }
    };

    loadTables();

    return () => {
      active = false;
    };
  }, [resetPreview, tenantId]);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => String(tenant.id) === String(tenantId)) || null,
    [tenantId, tenants]
  );

  const selectedTable = useMemo(
    () => tables.find((item) => item.name === table) || null,
    [table, tables]
  );

  const editableColumns = useMemo(
    () => asArray(selectedTable?.columns).filter((column) => column?.is_editable),
    [selectedTable]
  );
  const templateColumns = useMemo(
    () => templateColumnsFor(selectedTable?.name, editableColumns, templateColumnMode),
    [editableColumns, selectedTable, templateColumnMode]
  );

  const duplicateTargets = useMemo(() => findDuplicateTargets(mappings), [mappings]);
  const mappedRows = useMemo(
    () => mappings.filter((mapping) => mapping.db_column),
    [mappings]
  );
  const importDisabled = !preview?.import_id
    || mappedRows.length === 0
    || duplicateTargets.size > 0
    || importing
    || (mode === 'upsert' && !uniqueBy);
  const templateDisabled = !selectedTable
    || templateColumns.length === 0
    || loadingTables
    || previewing
    || importing
    || exportingTemplate;

  const handleTenantChange = (event) => {
    const nextTenantId = event.target.value;
    const tenant = tenants.find((item) => String(item.id) === String(nextTenantId));
    if (tenant) selectPlatformTenant(tenant);
    setTenantId(nextTenantId);
  };

  const handleTableChange = (event) => {
    setTable(event.target.value);
    resetPreview();
  };

  const handleFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);
    resetPreview();
  };

  const handleDownloadTemplate = async () => {
    if (!selectedTable || templateColumns.length === 0) {
      setError('Choose a table with editable columns before downloading a template.');
      return;
    }

    setExportingTemplate(true);
    setError('');

    try {
      const tableName = selectedTable.name;
      const tablePayload = await fetchTenantImportTables(tenantId, {
        table: tableName,
        includeLookupOptions: true,
      });
      const tableDetails = asArray(tablePayload?.tables)[0] || selectedTable;
      const exportColumns = templateColumnsFor(
        tableDetails.name,
        asArray(tableDetails.columns).filter((column) => column?.is_editable),
        templateColumnMode
      );

      if (exportColumns.length === 0) {
        setError('The selected template option has no editable columns for this table.');
        return;
      }

      setTables((current) => current.map((item) => (
        item.name === tableDetails.name ? { ...item, ...tableDetails } : item
      )));

      const ExcelJSModule = await import('exceljs');
      const ExcelJS = ExcelJSModule.default || ExcelJSModule;
      const workbook = new ExcelJS.Workbook();
      const guideRows = buildGuideRows(tableDetails.name, exportColumns);
      const headers = exportColumns.map((column) => column.name);
      const templateSheet = workbook.addWorksheet('Import_Template', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });
      const dropdownRanges = buildDropdownOptionSheet(workbook, tableDetails.name, exportColumns);

      templateSheet.addRow(headers);
      applyHeaderStyle(templateSheet.getRow(1));
      setExcelColumnWidths(templateSheet, [headers]);

      exportColumns.forEach((column, index) => {
        const dropdownRange = dropdownRanges.get(column.name);
        if (!dropdownRange) return;

        const columnLetter = excelColumnName(index + 1);
        for (let rowIndex = 2; rowIndex <= TEMPLATE_ROW_LIMIT + 1; rowIndex++) {
          const cell = templateSheet.getCell(`${columnLetter}${rowIndex}`);
          cell.dataValidation = {
            type: 'list',
            allowBlank: !column.is_required,
            formulae: [dropdownRange],
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Invalid option',
            error: `Choose a value from the ${column.name} dropdown list.`,
            showInputMessage: true,
            promptTitle: column.label || column.name,
            prompt: 'Choose a value from the dropdown list.',
          };
        }
      });

      const guideSheet = workbook.addWorksheet('Column_Guide', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      guideSheet.addRows(guideRows);
      applyHeaderStyle(guideSheet.getRow(1));
      setExcelColumnWidths(guideSheet, guideRows);

      const tenantPart = safeFilePart(selectedTenant?.org_code || selectedTenant?.key || selectedTenant?.display_name);
      const tablePart = safeFilePart(tableDetails.name);
      const buffer = await workbook.xlsx.writeBuffer();
      downloadWorkbookBuffer(buffer, `${tenantPart}-${tablePart}-import-template.xlsx`);
    } catch (downloadError) {
      console.error(downloadError);
      setError('Unable to create the import template. Refresh the table metadata and try again.');
    } finally {
      setExportingTemplate(false);
    }
  };

  const handlePreview = async () => {
    if (!tenantId || !table || !file) {
      setError('Select a tenant, table, and Excel file before preview.');
      return;
    }

    setPreviewing(true);
    setError('');
    setResult(null);

    try {
      const payload = await previewTenantDataImport({
        tenantId,
        table,
        file,
        headingRow,
        sheetIndex,
      });
      const initialMappings = buildInitialMappings(payload?.headers, payload?.columns);
      setPreview(payload);
      setMappings(initialMappings);
      setUniqueBy(chooseDefaultUniqueColumn(payload?.columns, initialMappings));
    } catch (requestError) {
      setPreview(null);
      setMappings([]);
      setError(mapLaravelError(requestError).message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleMappingChange = (excelColumn, dbColumn) => {
    setMappings((current) => current.map((mapping) => (
      mapping.excel_column === excelColumn ? { ...mapping, db_column: dbColumn } : mapping
    )));
  };

  const handleAutoMap = () => {
    if (!preview) return;
    const initialMappings = buildInitialMappings(preview.headers, preview.columns);
    setMappings(initialMappings);
    setUniqueBy(chooseDefaultUniqueColumn(preview.columns, initialMappings));
  };

  const handleClearMappings = () => {
    setMappings((current) => current.map((mapping) => ({ ...mapping, db_column: '' })));
    setUniqueBy('');
  };

  const handleRunImport = async () => {
    if (importDisabled) return;

    setImporting(true);
    setError('');
    setResult(null);

    try {
      const payload = await runTenantDataImport({
        tenantId,
        table,
        importId: preview.import_id,
        headingRow: preview.heading_row,
        sheetIndex: preview.sheet_index,
        mode,
        uniqueBy,
        mappings: mappedRows,
      });

      setResult(payload);
      setPreview((current) => (current ? { ...current, import_id: '' } : current));
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Tenant Data Import
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Map spreadsheet columns into an isolated tenant database table.
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => {
              resetPreview();
              loadTenants();
            }}
            disabled={loadingTenants || loadingTables || previewing || importing || exportingTemplate}
          >
            Refresh
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        <ResultAlert result={result} />

        <Paper variant="outlined" sx={{ p: 2 }}>
          {(loadingTenants || loadingTables) ? <LinearProgress sx={{ mb: 2 }} /> : null}

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth size="small" disabled={loadingTenants || importing}>
                <InputLabel id="tenant-import-tenant-label">Tenant</InputLabel>
                <Select
                  labelId="tenant-import-tenant-label"
                  label="Tenant"
                  value={tenantId}
                  onChange={handleTenantChange}
                >
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={String(tenant.id)}>
                      {tenant.display_name} ({tenant.org_code || tenant.key})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={!tenantId || loadingTables || importing}>
                <InputLabel id="tenant-import-table-label">Database Table</InputLabel>
                <Select
                  labelId="tenant-import-table-label"
                  label="Database Table"
                  value={table}
                  onChange={handleTableChange}
                >
                  {tables.map((item) => (
                    <MenuItem key={item.name} value={item.name}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {selectedTenant || selectedTable ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selectedTenant ? <Chip size="small" color="primary" label={selectedTenant.display_name} /> : null}
                {selectedTable ? <Chip size="small" label={selectedTable.name} /> : null}
                {editableColumns.length > 0 ? <Chip size="small" label={`${editableColumns.length} editable columns`} /> : null}
              </Stack>
            ) : null}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <FormControl
                size="small"
                disabled={!selectedTable || loadingTables || importing || exportingTemplate}
                sx={{ minWidth: 210, width: { xs: '100%', md: 210 } }}
              >
                <InputLabel id="tenant-import-template-columns-label">Template Columns</InputLabel>
                <Select
                  labelId="tenant-import-template-columns-label"
                  label="Template Columns"
                  value={templateColumnMode}
                  onChange={(event) => setTemplateColumnMode(event.target.value)}
                >
                  <MenuItem value="all">All editable columns</MenuItem>
                  <MenuItem value="required_core">Required/core fields</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleDownloadTemplate}
                disabled={templateDisabled}
                sx={{ minWidth: 190 }}
              >
                {exportingTemplate ? 'Preparing...' : 'Download Template'}
              </Button>

              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadOutlinedIcon />}
                disabled={!tenantId || !table || importing || exportingTemplate}
                sx={{ minWidth: 180 }}
              >
                Choose Excel
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls,.csv,.txt"
                  onChange={handleFileChange}
                />
              </Button>

              <TextField
                fullWidth
                size="small"
                label="Selected file"
                value={file?.name || ''}
                InputProps={{ readOnly: true }}
              />

              <TextField
                size="small"
                type="number"
                label="Header Row"
                value={headingRow}
                onChange={(event) => {
                  setHeadingRow(Math.max(1, Number(event.target.value || 1)));
                  resetPreview();
                }}
                inputProps={{ min: 1, max: 50 }}
                sx={{ width: { xs: '100%', md: 140 } }}
              />

              <TextField
                size="small"
                type="number"
                label="Sheet"
                value={sheetIndex}
                onChange={(event) => {
                  setSheetIndex(Math.max(0, Number(event.target.value || 0)));
                  resetPreview();
                }}
                inputProps={{ min: 0, max: 20 }}
                sx={{ width: { xs: '100%', md: 120 } }}
              />

              <Button
                variant="contained"
                onClick={handlePreview}
                disabled={!tenantId || !table || !file || previewing || importing || exportingTemplate}
                sx={{ minWidth: 140 }}
              >
                {previewing ? 'Previewing...' : 'Preview'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {preview ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Spreadsheet Preview
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={preview.file_name} />
                    <Chip size="small" label={`${asArray(preview.headers).length} Excel columns`} />
                    <Chip size="small" label={`${asArray(preview.sample_rows).length} sample rows`} />
                  </Stack>
                </Stack>
              </Stack>

              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 90 }}>Row</TableCell>
                      {asArray(preview.headers).map((header) => (
                        <TableCell key={header.column} sx={{ minWidth: 160 }}>
                          <Typography variant="caption" color="text.secondary">
                            {header.column}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {header.label}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {asArray(preview.sample_rows).map((row) => (
                      <TableRow key={row._row_number}>
                        <TableCell>{row._row_number}</TableCell>
                        {asArray(preview.headers).map((header) => (
                          <TableCell key={`${row._row_number}-${header.column}`}>
                            {row[header.column] || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Paper>
        ) : null}

        {preview ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Column Mapping
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {mappedRows.length} mapped columns
                    {duplicateTargets.size > 0 ? ` · ${duplicateTargets.size} duplicate targets` : ''}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Tooltip title="Auto map matching names">
                    <IconButton color="primary" onClick={handleAutoMap} disabled={importing}>
                      <AutoFixHighOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear mappings">
                    <IconButton onClick={handleClearMappings} disabled={importing}>
                      <ClearAllOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              {duplicateTargets.size > 0 ? (
                <Alert severity="warning">
                  Each database column can be mapped once.
                </Alert>
              ) : null}

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="tenant-import-mode-label">Import Mode</InputLabel>
                  <Select
                    labelId="tenant-import-mode-label"
                    label="Import Mode"
                    value={mode}
                    onChange={(event) => setMode(event.target.value)}
                    disabled={importing}
                  >
                    <MenuItem value="insert">Insert new rows</MenuItem>
                    <MenuItem value="upsert">Update matching rows or insert</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" disabled={mode !== 'upsert' || importing}>
                  <InputLabel id="tenant-import-unique-label">Match Column</InputLabel>
                  <Select
                    labelId="tenant-import-unique-label"
                    label="Match Column"
                    value={uniqueBy}
                    onChange={(event) => setUniqueBy(event.target.value)}
                  >
                    {editableColumns.map((column) => (
                      <MenuItem key={column.name} value={column.name}>
                        {column.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Divider />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 110 }}>Excel</TableCell>
                      <TableCell>Header</TableCell>
                      <TableCell>Sample</TableCell>
                      <TableCell sx={{ minWidth: 260 }}>Database Column</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {asArray(preview.headers).map((header) => {
                      const mapping = mappings.find((item) => item.excel_column === header.column) || {};
                      const sample = asArray(preview.sample_rows)
                        .map((row) => row[header.column])
                        .filter(Boolean)
                        .slice(0, 2)
                        .join(', ');
                      const isDuplicate = mapping.db_column && duplicateTargets.has(mapping.db_column);

                      return (
                        <TableRow key={header.column} hover>
                          <TableCell>
                            <Chip size="small" label={header.column} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {header.label}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <Typography variant="body2" color="text.secondary" noWrap title={sample}>
                              {sample || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small" error={Boolean(isDuplicate)}>
                              <Select
                                value={mapping.db_column || ''}
                                onChange={(event) => handleMappingChange(header.column, event.target.value)}
                                displayEmpty
                                disabled={importing}
                              >
                                <MenuItem value="">Ignore</MenuItem>
                                {editableColumns.map((column) => (
                                  <MenuItem key={column.name} value={column.name}>
                                    <Stack spacing={0.25}>
                                      <Typography variant="body2">{column.name}</Typography>
                                      {formatColumnMeta(column) ? (
                                        <Typography variant="caption" color="text.secondary">
                                          {formatColumnMeta(column)}
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<PlayArrowOutlinedIcon />}
                  onClick={handleRunImport}
                  disabled={importDisabled}
                >
                  {importing ? 'Importing...' : 'Run Import'}
                </Button>
              </Stack>

              {importing ? <LinearProgress /> : null}
            </Stack>
          </Paper>
        ) : null}

        {asArray(result?.errors).length > 0 ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Row Errors
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 120 }}>Row</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {asArray(result.errors).map((item) => (
                      <TableRow key={`${item.row}-${item.message}`}>
                        <TableCell>{item.row}</TableCell>
                        <TableCell>{item.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Box>
  );
}
